/**
 * Copyright (c) 2025, CheckPlus Limited
 */
import React, { Component } from "react";
import PropTypes from "prop-types";
import "./Portfolio.css";

// The "you" view a business and an individual share: one commitment at the
// top, one split underneath, and what it adds up to on the right. A business
// commits a rule against sales; an individual commits an amount.
class Portfolio extends Component {
  renderBusinessCommit() {
    const vm = this.props.vm;
    const p = vm.portfolio;
    return (
      <React.Fragment>
        <div className="Marketplace-Portfolio-rules">
          {p.ruleOptions.map((r) => (
            <div
              className={"Marketplace-Portfolio-rule" + (r.active ? " Marketplace-Portfolio-rule--active" : "")}
              onClick={r.pick}
              key={r.id}
            >
              <div className="Marketplace-Portfolio-ruleLabel">{r.label}</div>
              <div className="Marketplace-Portfolio-ruleHint">{r.hint}</div>
            </div>
          ))}
        </div>
        <div className="Marketplace-Portfolio-dial">
          <div className="Marketplace-Portfolio-slider">
            <div className="Marketplace-Portfolio-sliderHead">
              <span className="Marketplace-Portfolio-sliderLabel">{p.rateLabel}</span>
              <span className="Marketplace-Portfolio-sliderValue">{p.rateValue}</span>
            </div>
            <input
              type="range"
              min={p.rateMin}
              max={p.rateMax}
              step={p.rateStep}
              value={p.rateRaw}
              onChange={p.onRate}
              className="Marketplace-Portfolio-range"
            />
            <div className="Marketplace-Portfolio-sliderEnds">
              <span>{p.rateMinLabel}</span>
              <span>{p.rateMaxLabel}</span>
            </div>
          </div>
          <div className="Marketplace-Portfolio-dialDivider" />
          <div className="Marketplace-Portfolio-aside">
            <div className="Marketplace-cardTitle">{vm.m.thisMonthLabel}</div>
            <div className="Marketplace-Portfolio-asideValue">{p.monthlyLabel}</div>
            <div className="Marketplace-Portfolio-asideNote">{p.basis}</div>
          </div>
        </div>
        <div className="Marketplace-Portfolio-cap">
          <div
            className={"Marketplace-toggle" + (p.capOn ? " Marketplace-toggle--on" : "")}
            onClick={p.toggleCap}
          >
            <span className="Marketplace-toggleKnob" />
          </div>
          <span className="Marketplace-Portfolio-capLine">{p.capLine}</span>
        </div>
      </React.Fragment>
    );
  }

  renderIndividualCommit() {
    const vm = this.props.vm;
    const p = vm.portfolio;
    return (
      <React.Fragment>
        {/* One-time or recurring first, then how often a recurring gift
            repeats. The cadence row only appears once recurring is chosen, so
            a one-time giver is never asked a question that doesn't apply. */}
        {/* No modes while recurring is hidden, and one mode is not a choice. */}
        {!!p.freqModes.length && (
          <div className="Marketplace-Portfolio-freq">
            {p.freqModes.map((f) => (
              <div
                className={"Marketplace-Portfolio-freqTab" + (f.active ? " Marketplace-Portfolio-freqTab--active" : "")}
                onClick={f.pick}
                key={f.id}
              >
                {f.label}
              </div>
            ))}
          </div>
        )}
        {p.isRecurring && (
          <div className="Marketplace-Portfolio-cadences">
            {p.freqCadences.map((f) => (
              <div
                className={
                  "Marketplace-Portfolio-cadence" +
                  (f.active ? " Marketplace-Portfolio-cadence--active" : "")
                }
                onClick={f.pick}
                key={f.id}
              >
                {f.label}
              </div>
            ))}
          </div>
        )}
        {p.isOnceFreq && (
          <div className="Marketplace-Portfolio-onceNote">{p.onceNote}</div>
        )}
        <div className="Marketplace-Portfolio-presets">
          {p.presets.map((v) => (
            <div
              className={"Marketplace-Portfolio-preset" + (v.active ? " Marketplace-Portfolio-preset--active" : "")}
              onClick={v.pick}
              key={v.id}
            >
              {v.label}
            </div>
          ))}
          {/* The presets and the slider stop at HK$3,000; this is how a donor
              gives more than that, or an exact figure. */}
          <div
            className={
              "Marketplace-Portfolio-preset Marketplace-Portfolio-preset--custom" +
              (p.customAmountChip.active ? " Marketplace-Portfolio-preset--active" : "")
            }
            onClick={p.customAmountChip.pick}
          >
            {p.customAmountChip.label}
          </div>
        </div>
        {p.isCustomAmount && (
          <div className="Marketplace-Portfolio-customAmount">
            <div className="Marketplace-Portfolio-customField">
              <span className="Marketplace-Portfolio-customPrefix">HK$</span>
              <input
                type="text"
                inputMode="numeric"
                className="Marketplace-Portfolio-customInput"
                value={p.customAmountValue}
                onChange={p.onCustomAmount}
                placeholder={vm.m.customAmountPlaceholder}
                aria-label={p.customAmountChip.label}
              />
            </div>
            {p.amountTooLow && (
              <div className="Marketplace-Portfolio-customHint">{p.amountTooLowNote}</div>
            )}
          </div>
        )}
        <div className="Marketplace-Portfolio-dial">
          <div className="Marketplace-Portfolio-slider">
            <div className="Marketplace-Portfolio-sliderHead">
              <span className="Marketplace-Portfolio-sliderLabel">{vm.m.ownAmount}</span>
              <span className="Marketplace-Portfolio-sliderValue">{p.amountLabel}</span>
            </div>
            <input
              type="range"
              min={p.amountMin}
              max={p.amountMax}
              step="10"
              value={p.amountRaw}
              onChange={p.onAmount}
              className="Marketplace-Portfolio-range"
            />
            <div className="Marketplace-Portfolio-sliderEnds">
              <span>{p.amountMinLabel}</span>
              <span>{p.amountMaxLabel}</span>
            </div>
          </div>
          <div className="Marketplace-Portfolio-dialDivider" />
          <div className="Marketplace-Portfolio-aside">
            <div className="Marketplace-cardTitle">{p.asideTitle}</div>
            <div className="Marketplace-Portfolio-asideValue">{p.asideValue}</div>
            <div className="Marketplace-Portfolio-asideNote">
              {p.isOnceFreq ? vm.m.asideOnceNote : vm.m.asideChange}
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  }

  render() {
    const vm = this.props.vm;
    const p = vm.portfolio;
    return (
      <div className="Marketplace-Portfolio">
        <div className="Marketplace-Portfolio-main">
          <div className="Marketplace-card">
            <div className="Marketplace-cardTitle">{p.commitTitle}</div>
            {vm.isBiz ? this.renderBusinessCommit() : this.renderIndividualCommit()}
          </div>

          <div className="Marketplace-card">
            <div className="Marketplace-cardHead">
              <span className="Marketplace-cardTitle">{vm.m.splitTitle}</span>
              {p.rows.length > 1 && (
                <span className="Marketplace-link" onClick={p.equalize}>
                  {vm.m.evenItOut}
                </span>
              )}
              {/* A recurring plan is a donor's to hold. Nothing to pause about
                  a single gift, and a business rule is governed by the rule. */}
              {p.canPause && (
                <div
                  className={
                    "Marketplace-Portfolio-pause" +
                    (p.paused ? " Marketplace-Portfolio-pause--on" : "")
                  }
                  onClick={p.togglePause}
                >
                  {p.pauseLabel}
                </div>
              )}
            </div>
            <div className="Marketplace-Portfolio-splitSub">{p.splitSub}</div>
            {p.paused && (
              <div className="Marketplace-Portfolio-pausedNote">{vm.m.planPausedNote}</div>
            )}
            {/* The same amount as the slider above, editable in place — a donor
                adjusting an existing plan comes here, not to the presets. */}
            {!vm.isBiz && !!p.rows.length && (
              <div className="Marketplace-Portfolio-totalRow">
                <span className="Marketplace-Portfolio-totalLabel">{p.totalLabel}</span>
                <div className="Marketplace-Portfolio-stepper">
                  <div className="Marketplace-Portfolio-stepBtn" onClick={p.totalDown}>
                    −
                  </div>
                  <div className="Marketplace-Portfolio-stepField">
                    <span className="Marketplace-Portfolio-stepPrefix">HK$</span>
                    <input
                      type="number"
                      min={p.amountMin}
                      step="10"
                      value={p.totalRaw}
                      onChange={p.onTotal}
                      className="Marketplace-Portfolio-stepInput"
                      aria-label={p.totalLabel}
                    />
                  </div>
                  <div className="Marketplace-Portfolio-stepBtn" onClick={p.totalUp}>
                    +
                  </div>
                </div>
                <span className="Marketplace-Portfolio-totalNote">{p.totalNote}</span>
              </div>
            )}
            {!p.rows.length && (
              <div className="Marketplace-empty">
                <div className="Marketplace-emptyTitle">{vm.m.planEmptyTitle}</div>
                <div>{vm.m.planEmptyBody}</div>
              </div>
            )}
            <div className="Marketplace-Portfolio-rows">
              {p.rows.map((r) => (
                <div className="Marketplace-Portfolio-row" key={r.id}>
                  <div className="Marketplace-mono" style={{ background: r.monoBg, color: r.monoFg }}>
                    {r.logo ? <img src={r.logo} alt={r.name} className="Marketplace-monoImg" /> : r.mono}
                  </div>
                  {/* The design shows a cause under each name, but an org's
                      `cat` is a placeholder palette bucket, not a real
                      category — see the cause panel note in Marketplace.js. */}
                  <div className="Marketplace-Portfolio-rowInfo">
                    <div className="Marketplace-Portfolio-rowName">{r.name}</div>
                  </div>
                  {p.rows.length > 1 && (
                    <input
                      type="range"
                      // The track always spans the full 0-100% — the actual
                      // reachable value is clamped in setShare (Marketplace.js)
                      // to this org's own dollar-floor share and whatever room
                      // the other unlocked orgs' floors leave. Dragging past
                      // either edge just has no further effect (the thumb
                      // stays put) instead of the track itself shrinking.
                      min="0"
                      max="100"
                      value={r.share}
                      onChange={r.onShare}
                      disabled={r.immovable}
                      className="Marketplace-Portfolio-rowRange"
                    />
                  )}
                  <span className="Marketplace-Portfolio-rowPct">{p.rows.length > 1 ? r.pct : ""}</span>
                  <span className="Marketplace-Portfolio-rowAmount">{r.amount}</span>
                  {p.rows.length > 2 && (
                    <span
                      className={
                        "Marketplace-Portfolio-rowLock" + (r.locked ? " Marketplace-Portfolio-rowLock--active" : "")
                      }
                      onClick={r.toggleLock}
                      title={r.locked ? vm.m.unlockShare : vm.m.lockShare}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M12 1a5 5 0 0 0-5 5v3H6a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2h-1V6a5 5 0 0 0-5-5zM9 9V6a3 3 0 0 1 6 0v3H9z" />
                      </svg>
                    </span>
                  )}
                  <span className="Marketplace-Portfolio-rowRemove" onClick={r.remove}>
                    ×
                  </span>
                </div>
              ))}
            </div>
            <div className="Marketplace-Portfolio-add" onClick={p.goDiscover}>
              {vm.m.allocateAddAnother}
            </div>
          </div>
        </div>

        <div className="Marketplace-Portfolio-side">
          <div className="Marketplace-card">
            <div className="Marketplace-cardTitle">{p.summaryLabel}</div>
            <div className="Marketplace-Portfolio-total">{p.monthlyLabel}</div>
            <div className="Marketplace-Portfolio-summarySub">{p.summarySub}</div>
            {/* A paused plan has nothing to confirm — the note in the split
                card says why, so this would only contradict it. */}
            <div
              className={
                "Marketplace-btnPrimary Marketplace-btn--block" +
                (p.rows.length && !p.amountTooLow && !p.paused
                  ? ""
                  : " Marketplace-btn--inert")
              }
              onClick={
                p.rows.length && !p.amountTooLow && !p.paused ? p.confirm : undefined
              }
            >
              {p.confirmLabel}
            </div>
            <div className="Marketplace-Portfolio-receiptNote">{p.receiptNote}</div>
          </div>

          {!!p.impactRows.length && (
            <div className="Marketplace-card">
              <div className="Marketplace-cardTitle">{p.impactTitle}</div>
              <div className="Marketplace-Portfolio-impact">
                {p.impactRows.map((i) => (
                  <div className="Marketplace-Portfolio-impactRow" key={i.id}>
                    <span className="Marketplace-Portfolio-impactDot" />
                    <span>{i.text}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Receipts belong here rather than beside Confirm: the plan on this
              page hasn't been given yet, so the only receipts that exist are
              for the gifts listed below. */}
          {/* What is committed but not yet charged — one row per live
              recurring series, soonest first. Absent when there are none. */}
          {!!p.upcoming && (
            <div className="Marketplace-Portfolio-upcoming">
              <div className="Marketplace-Portfolio-upcomingTitle">{p.upcoming.title}</div>
              <div className="Marketplace-Portfolio-upcomingTotal">{p.upcoming.total}</div>
              <div className="Marketplace-Portfolio-upcomingWhen">{p.upcoming.when}</div>
              <div className="Marketplace-Portfolio-upcomingRows">
                {p.upcoming.rows.map((u) => (
                  <React.Fragment key={u.id}>
                    <div className="Marketplace-Portfolio-upcomingRow">
                      <span className="Marketplace-Portfolio-upcomingWho">{u.who}</span>
                      <span className="Marketplace-Portfolio-upcomingMeta">
                        {u.every ? u.when + " · " + u.every : u.when}
                      </span>
                      <span className="Marketplace-Portfolio-upcomingAmount">{u.amount}</span>
                      {u.canEdit && (
                        <span
                          className="Marketplace-Portfolio-upcomingEdit"
                          onClick={u.edit}
                        >
                          {vm.m.editScheduled}
                        </span>
                      )}
                    </div>
                    {/* Only the server can change a scheduled payment, and only
                        while it is still scheduled — so a failure here is shown
                        rather than swallowed. */}
                    {p.upcoming.editing === u.paymentId && (
                      <div className="Marketplace-Portfolio-editor">
                        <div className="Marketplace-Portfolio-editorFields">
                          <label className="Marketplace-Portfolio-editorField">
                            <span className="Marketplace-Portfolio-editorLabel">
                              {vm.m.editAmountLabel}
                            </span>
                            <input
                              type="number"
                              min="1"
                              step="10"
                              value={p.upcoming.form.amount}
                              onChange={p.upcoming.form.onAmount}
                              className="Marketplace-Portfolio-editorInput"
                            />
                          </label>
                          <label className="Marketplace-Portfolio-editorField">
                            <span className="Marketplace-Portfolio-editorLabel">
                              {vm.m.editEveryLabel}
                            </span>
                            <select
                              value={p.upcoming.form.interval}
                              onChange={p.upcoming.form.onInterval}
                              className="Marketplace-Portfolio-editorSelect"
                            >
                              {p.upcoming.intervalOptions.map((o) => (
                                <option value={o.id} key={o.id}>
                                  {o.label}
                                </option>
                              ))}
                            </select>
                          </label>
                        </div>
                        {!!p.upcoming.form.error && (
                          <div className="Marketplace-Portfolio-editorError">
                            {p.upcoming.form.error}
                          </div>
                        )}
                        {p.upcoming.form.confirm && (
                          <div className="Marketplace-Portfolio-editorWarn">
                            {vm.m.editCancelWarn}
                          </div>
                        )}
                        <div className="Marketplace-Portfolio-editorActions">
                          <div
                            className={
                              "Marketplace-Portfolio-editorSave" +
                              (p.upcoming.form.saving
                                ? " Marketplace-Portfolio-editorSave--busy"
                                : "")
                            }
                            onClick={
                              p.upcoming.form.saving ? undefined : p.upcoming.form.save
                            }
                          >
                            {p.upcoming.form.saving ? vm.m.editSaving : vm.m.editSave}
                          </div>
                          <div
                            className="Marketplace-Portfolio-editorGhost"
                            onClick={p.upcoming.form.close}
                          >
                            {vm.m.editClose}
                          </div>
                          {/* Ending a series takes a second, explicit click. */}
                          <div
                            className="Marketplace-Portfolio-editorStop"
                            onClick={
                              p.upcoming.form.confirm
                                ? p.upcoming.form.confirmCancel
                                : p.upcoming.form.askCancel
                            }
                          >
                            {p.upcoming.form.confirm
                              ? vm.m.editCancelConfirm
                              : vm.m.editCancelSeries}
                          </div>
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}

          {/* Absent entirely for a business or a signed-out visitor: neither has
              a giving history, and the panel used to fill itself with sample
              rows rather than admit that. */}
          {!!p.history && (
            <div className="Marketplace-Portfolio-history">
              <div className="Marketplace-cardTitle">{vm.m.historyTitle}</div>
              {/* Completed gifts only — a scheduled payment has not happened yet,
                so it is not history. Without this the panel was a title and a
                button with nothing between them. */}
              {(p.history.loading || p.history.error) && (
                <div className="Marketplace-Portfolio-historyNote">
                  {p.history.error ? vm.m.dashError : vm.m.dashLoading}
                </div>
              )}
              {p.history.empty && (
                <div className="Marketplace-Portfolio-historyNote">{vm.m.myDashEmpty}</div>
              )}
              {p.history.rows.map((h) => (
                <div className="Marketplace-Portfolio-historyRow" key={h.id}>
                  <span className="Marketplace-Portfolio-historyDate">{h.date}</span>
                  <span className="Marketplace-Portfolio-historyAmount">{h.amount}</span>
                </div>
              ))}
              {/* Hidden for now — see NOT_READY in Marketplace.js. */}
              {!!p.showReceiptDownload &&
              (p.latestReceiptUrl ? (
                <a
                  className="Marketplace-Portfolio-receiptBtn Marketplace-Portfolio-receiptBtn--history"
                  href={p.latestReceiptUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {vm.m.downloadReceipt}
                </a>
              ) : (
                <div className="Marketplace-Portfolio-receiptBtn Marketplace-Portfolio-receiptBtn--history Marketplace-Portfolio-receiptBtn--none">
                  {vm.m.downloadReceipt}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }
}

Portfolio.propTypes = {
  vm: PropTypes.object.isRequired,
};

export default Portfolio;
